export const TEMPERATURE_RULES = {
    // 分区色温分析规则
    // 色温基调规则
    PARTITION_TEMPERATURE_TONE: {
        condition: (structuredConclusions) => {
            const rules = [];
            const tone = structuredConclusions.colorTemperature.tone;
            if (tone) {
                const toneRule = `tone${tone.toUpperCase()}`;
                rules.push(toneRule);
            }
            return rules;
        }
    },

    // 色温冷暖倾向规则
    PARTITION_TEMPERATURE_INTENSITY: {
        condition: (structuredConclusions) => {
            const rules = [];
            const intensity = structuredConclusions.colorTemperature.intensity;

            if (intensity === "STRONG") {
                rules.push("intensityStrong");
            } else if (intensity === "MODERATE") {
                rules.push("intensityModerate");
            } else if (intensity === "WEAK") {
                rules.push("intensityWeak");
            }

            return rules;
        }
    },

    // 色温稳定性规则
    PARTITION_TEMPERATURE_STABILITY: {
        condition: (analysis) => {
            const rules = [];
            const b = analysis.b.statistics;
            const modeMedianDiff = Math.abs(b.mode - b.median);
            const modeMeanDiff = Math.abs(b.mode - b.mean);

            if (modeMedianDiff < 5 && modeMeanDiff < 5) {
                rules.push("stabilityStable");
            } else if (modeMedianDiff >= 5 || modeMeanDiff >= 5) {
                if (b.mean < 128 && b.mode < 128 && b.median < 128) {
                    rules.push("stabilityDominantCool");
                    if (b.skewness > 0) {
                        rules.push("stabilityWarmHighlights");
                    }
                } else if (b.mean < 128 && (b.mode >= 128 || b.median >= 128)) {
                    rules.push("stabilityCoolWithWarmInfluence");
                } else if (b.mean > 128 && b.mode < 128) {
                    rules.push("stabilityWarmWithCoolContrast");
                } else if (Math.abs(b.skewness) > 0.5) {
                    rules.push("stabilityBalancedContrast");
                } else {
                    rules.push("stabilityUniform");
                }
            }

            return rules;
        }
    },
    // 基本色温特征
    BASIC_TEMPERATURE: {
        condition: (global, _partitions) => {
            const temperatures = [];

            if (global.colorTemperature.tone === "STRONG_YELLOW" && global.colorTemperature.intensity === "STRONG") {
                temperatures.push("strongWarm");
            } else if (global.colorTemperature.tone === "STRONG_BLUE" && global.colorTemperature.intensity === "STRONG") {
                temperatures.push("strongCool");
            } else if (global.colorTemperature.tone === "SLIGHT_YELLOW" && global.colorTemperature.intensity === "MODERATE") {
                temperatures.push("slightWarm");
            } else if (global.colorTemperature.tone === "SLIGHT_BLUE" && global.colorTemperature.intensity === "MODERATE") {
                temperatures.push("slightCool");
            } else if (global.colorTemperature.tone === "NEUTRAL" && global.colorTemperature.intensity === "WEAK") {
                temperatures.push("neutral");
            }

            return temperatures;
        }
    },

    // 色温分布特征
    DISTRIBUTION_FEATURES: {
        condition: (global, partitions) => {
            const distributions = [];

            if (global.colorTemperature.tone.includes("YELLOW") &&
                Object.values(partitions).every(p => p?.colorTemperature?.tone?.includes("YELLOW"))) {
                distributions.push("consistentWarm");
            }
            if (global.colorTemperature.tone.includes("BLUE") &&
                Object.values(partitions).every(p => p?.colorTemperature?.tone?.includes("BLUE"))) {
                distributions.push("consistentCool");
            }
            if ((partitions.blacks?.colorTemperature?.tone?.includes("YELLOW") ||
                partitions.shadows?.colorTemperature?.tone?.includes("YELLOW")) &&
                (partitions.highlights?.colorTemperature?.tone?.includes("BLUE") ||
                    partitions.whites?.colorTemperature?.tone?.includes("BLUE"))) {
                distributions.push("warmShadowsCoolHighlights");
            }
            if ((partitions.blacks?.colorTemperature?.tone?.includes("BLUE") ||
                partitions.shadows?.colorTemperature?.tone?.includes("BLUE")) &&
                (partitions.highlights?.colorTemperature?.tone?.includes("YELLOW") ||
                    partitions.whites?.colorTemperature?.tone?.includes("YELLOW"))) {
                distributions.push("coolShadowsWarmHighlights");
            }
            if (global.colorTemperature.tone === "NEUTRAL" &&
                Object.values(partitions).some(p => p?.colorTemperature?.contrast)) {
                distributions.push("neutralWithContrast");
            }

            return distributions;
        }
    },

    PARTITION_TEMPERATURE_DEVIATION: {
        condition: (structuredConclusions, analysis) => {
            const b = analysis.b.statistics;
            const rules = [];
            const tempDifference = Math.abs(b.mean - 128);

            // 只根据偏差程度判断强弱
            if (tempDifference < 5) {
                rules.push('DEVIATION_WEAK');
            } else if (tempDifference < 10) {
                rules.push('DEVIATION_MODERATE');
            } else {
                rules.push('DEVIATION_STRONG');
            }

            return rules;
        }
    },

    // 新增规则
    // 全局-分区一致性规则
    OVERALL_TEMP_CONSISTENCY: {
        condition: (globalTemp, partitionsData) => {
            if (!globalTemp?.colorTemperature?.tone) return false;

            const { blacks, shadows, midtones, highlights, whites } = partitionsData;
            const partitions = [blacks, shadows, midtones, highlights, whites].filter(Boolean);

            let consistentCount = 0;
            partitions.forEach(partition => {
                if (partition?.colorTemperature?.tone === globalTemp.colorTemperature.tone &&
                    partition.colorTemperature.intensity === globalTemp.colorTemperature.intensity) {
                    consistentCount++;
                }
            });

            return consistentCount >= 3;
        }
    },

    // 全局-分区差异性规则
    OVERALL_TEMP_VARIATIONS: {
        condition: (globalTemp, partitionsData) => {
            if (!globalTemp?.colorTemperature?.tone) return null;

            const { blacks, shadows, midtones, highlights, whites } = partitionsData;
            const partitions = [
                { name: 'blacks', data: blacks },
                { name: 'shadows', data: shadows },
                { name: 'midtones', data: midtones },
                { name: 'highlights', data: highlights },
                { name: 'whites', data: whites }
            ].filter(p => p.data);

            // 判断色温类型的辅助函数
            const getTempType = (tone) => {
                if (!tone) return null;
                if (tone === 'NEUTRAL') return 'NEUTRAL';
                if (tone.includes('YELLOW')) return 'YELLOW';
                if (tone.includes('BLUE')) return 'BLUE';
                return null;
            };

            const globalTempType = getTempType(globalTemp.colorTemperature.tone);

            // 收集与全局色温不一致的分区
            const divergentPartitions = partitions
                .filter(partition => {
                    const colorTemp = partition.data?.colorTemperature;
                    if (!colorTemp?.tone) return false;

                    const partitionTempType = getTempType(colorTemp.tone);
                    // 检查色温类型是否与全局不同
                    return partitionTempType !== globalTempType;
                })
                .map(partition => ({
                    name: partition.name,
                    tone: partition.data.colorTemperature.tone,
                    intensity: partition.data.colorTemperature.intensity
                }));


            return divergentPartitions.length >= 1 ? divergentPartitions : null;
        }
    },

    // 明暗分区色温对比规则
    SHADOW_HIGHLIGHT_TEMP_CONTRAST: {
        condition: (globalTemp, partitionsData) => {
            const { shadows, highlights } = partitionsData;
            if (!shadows?.colorTemperature || !highlights?.colorTemperature) return false;

            // 检查色温对比
            const isColorContrast = (
                (shadows.colorTemperature.tone.includes('YELLOW') && highlights.colorTemperature.tone.includes('BLUE')) ||
                (shadows.colorTemperature.tone.includes('BLUE') && highlights.colorTemperature.tone.includes('YELLOW'))
            );

            return isColorContrast;
        }
    }
};